import {
  Warehouse,
  Package,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { StockSummary } from '../../../types';

interface InventoryTabProps {readonly stockLevels?: StockSummary[];}

export function InventoryTab({ stockLevels }: InventoryTabProps) {
  if (!stockLevels?.length) {
    return (
      <EmptyState
        icon={Warehouse}
        title="No stock data available"
        description="Stock levels for this item will appear here once inventory is tracked."
      />
    );
  }

  const totalStock = stockLevels.reduce((sum, s) => sum + s.currentQuantity, 0);
  const unit = stockLevels[0]?.unit ?? '';

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-gray-50 p-4">
        <p className="text-sm text-gray-500">Total Stock Across All Sites</p>
        <p className="text-3xl font-bold text-gray-900">
          {totalStock.toLocaleString()} {unit}
        </p>
      </div>

      <div>
        <h3 className="font-medium text-gray-900 mb-4">Stock by Location</h3>
        <div className="space-y-3">
          {stockLevels.map((stock) => (
            <StockLocationCard key={`${stock.siteId}-${stock.storageLocation}`} stock={stock} />
          ))}
        </div>
      </div>
    </div>
  );
}

interface StockLocationCardProps {readonly stock: StockSummary;}

function StockLocationCard({ stock }: StockLocationCardProps) {
  const isLow = stock.currentQuantity <= stock.reorderPoint;
  const isOut = stock.currentQuantity === 0;

  const getStatusLabel = () => {
    if (isOut) return 'OUT OF STOCK';
    if (isLow) return 'LOW STOCK';
    return null;
  };

  const getStatusColor = () => {
    if (isOut) return 'text-red-600';
    if (isLow) return 'text-amber-600';
    return 'text-gray-900';
  };

  const statusLabel = getStatusLabel();
  const statusColor = getStatusColor();

  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-gray-900">{stock.siteName}</p>
          <p className="text-sm text-gray-500">{stock.storageLocation}</p>
        </div>
        <div className="text-right">
          <p className={cn('text-lg font-semibold', statusColor)}>
            {stock.currentQuantity} {stock.unit}
          </p>
          {statusLabel && (
            <span className={cn('text-xs font-medium', statusColor)}>
              {statusLabel}
            </span>
          )}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
        <span>Reorder point: {stock.reorderPoint}</span>
        <span>Updated: {new Date(stock.lastUpdated).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

interface EmptyStateProps {
  readonly icon: typeof Package;
  readonly title: string;
  readonly description: string;
}

function EmptyState({
  icon: emptyIcon,
  title,
  description,
}: EmptyStateProps) {
  const IconComponent = emptyIcon;
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-gray-100 p-4 mb-4">
        <IconComponent className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 max-w-sm">{description}</p>
    </div>
  );
}
